import React, { memo, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import BaseInput from "components/BaseInput";
import SearchAddressInput from "pages/OrderModal/components/SearchAddressInput";
import SearchBuildingInput from "pages/OrderModal/components/SearchBuildingInput";
import { IFormValues } from "components/BaseInput/types";
import {
  IFoundPlaces,
  IHouse,
} from "pages/OrderModal/components/SearchAddressInput/types";
import { useFormik } from "formik";
import { useGetGeoDataQuery } from "store/api";
import { useAppSelector } from "store/hooks/useAppSelector";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import {
  removeEntrance,
  removeSrcAddress,
  removeSrcBuilding,
  setEntrance,
  setSrcAddress,
  setSrcBuilding,
  setSrcAddressRoute,
  removeSrcAddressRoute,
} from "store/slices/srcAddress";
import styles from "./SrcAddress.styled";

const SrcAddress = () => {
  const address = useAppSelector((state) => state.srcAddress);
  const { srcAddressRoute } = useAppSelector((state) => state.srcAddress);
  const [searchAddressValue, setSearchAddressValue] = useState<string>("");
  const [foundPlaces, setFoundPlaces] = useState<IFoundPlaces[]>([]);
  const dispatch = useAppDispatch();
  const { data, isLoading } = useGetGeoDataQuery(
    { q: searchAddressValue },
    {
      skip: searchAddressValue.length < 3,
    },
  );

  const formik = useFormik<IFormValues>({
    initialValues: {
      entrance: address.entrance,
    },
    onSubmit: (values) => {
      console.log(values);
    },
  });

  useEffect(() => {
    if (data) {
      const foundPlacesData = [
        ...data.geo_streets.geo_street,
        ...data.geo_objects.geo_object,
      ];
      setFoundPlaces(foundPlacesData);
    }
  }, [data]);

  useEffect(() => {
    if (formik.values.entrance) {
      dispatch(setEntrance({ entrance: formik.values.entrance }));
    } else {
      dispatch(removeEntrance());
    }
  }, [formik.values.entrance]);

  const handleAddressSearch = (newSearchValue: string) => {
    if (newSearchValue) {
      setSearchAddressValue(newSearchValue);
    } else {
      setSearchAddressValue("");
      dispatch(removeSrcAddress());
      dispatch(removeSrcBuilding());
      dispatch(removeEntrance());
      dispatch(removeSrcAddressRoute());
      formik.setFieldValue("entrance", "");
    }
  };

  const handleSelectedAddress = (value: IFoundPlaces | null) => {
    if (value) {
      if (value.houses?.length) {
        const srcAddress = {
          name: value.name,
          ["old_name"]: value.old_name,
          houses: value.houses,
          locale: value.locale,
        };
        dispatch(setSrcAddress(srcAddress));
        dispatch(
          setSrcAddressRoute({
            ...srcAddressRoute,
            name: value.name,
          }),
        );
      } else {
        const srcAddress = {
          ...address.address,
          name: value.name,
          locale: value.locale,
        };
        dispatch(setSrcAddress(srcAddress));
        dispatch(
          setSrcAddressRoute({
            ...srcAddressRoute,
            name: value.name,
            lat: value.lat,
            lng: value.lng,
          }),
        );
      }
    }
  };

  const handleSelectedBuilding = (value: IHouse | null) => {
    if (value) {
      const srcBuilding = {
        house: value.house,
        lat: value.lat,
        lng: value.lng,
      };
      dispatch(setSrcBuilding(srcBuilding));
      dispatch(
        setSrcAddressRoute({
          ...srcAddressRoute,
          number: value.house,
          lat: value.lat,
          lng: value.lng,
        }),
      );
    }
  };

  return (
    <Box sx={styles.srcAddressWrap}>
      <SearchAddressInput
        name="srcAddress"
        initialValue={address.address.name}
        data={foundPlaces}
        onSearch={handleAddressSearch}
        label="Звідки"
        onSelectValue={handleSelectedAddress}
      />
      {isLoading && (
        <CircularProgress color="inherit" size="24px" sx={styles.progress} />
      )}
      <Box sx={styles.srcBuildWrap}>
        <SearchBuildingInput
          name="srcBuilding"
          label="Будинок"
          initialValue={address.building.house}
          data={address.address.houses || []}
          onSelectValue={handleSelectedBuilding}
          disabled={!address.address.houses?.length}
        />
        <BaseInput
          id="entrance"
          name="entrance"
          type="text"
          label="Підʼїзд"
          formik={formik}
          initialValue={address.entrance}
          disabled={!address.building.house}
        />
      </Box>
    </Box>
  );
};

export default memo(SrcAddress);
